import { forwardRef } from 'react';
import type { TextareaHTMLAttributes } from 'react';

interface TextareaWithCountProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  value: string;
  maxLength: number;
  label?: string;
  helpText?: string;
  error?: string;
}

export const TextareaWithCount = forwardRef<HTMLTextAreaElement, TextareaWithCountProps>(
  ({ value, maxLength, label, helpText, error, className = '', required, ...props }, ref) => {
    const remaining = maxLength - value.length;

    // Determine counter color based on remaining characters
    let counterColor = 'text-slate-600 dark:text-slate-400'; // Normal
    if (remaining <= 20 && remaining > 0) {
      counterColor = 'text-warning-600 dark:text-warning-400'; // Warning
    } else if (remaining === 0) {
      counterColor = 'text-danger-600 dark:text-danger-400'; // At limit
    }

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-semibold text-navy-800 dark:text-gold-100 mb-2">
            {label}
            {required && <span className="text-danger-600 ml-1">*</span>}
          </label>
        )}

        <textarea
          ref={ref}
          value={value}
          maxLength={maxLength}
          className={`
            w-full px-4 py-3
            bg-white dark:bg-navy-700
            border-2 rounded-lg
            text-navy-800 dark:text-gold-100
            placeholder-slate-400 dark:placeholder-slate-500
            transition-all duration-200
            resize-vertical
            min-h-[120px]
            ${error
              ? 'border-danger-500 focus:border-danger-600 focus:ring-4 focus:ring-danger-200 dark:focus:ring-danger-900/20'
              : 'border-slate-300 dark:border-navy-600 focus:border-teal-500 dark:focus:border-teal-400 focus:ring-4 focus:ring-teal-200 dark:focus:ring-teal-900/20'
            }
            disabled:opacity-50 disabled:cursor-not-allowed
            ${className}
          `}
          {...props}
        />

        <div className="flex justify-between items-center mt-2 text-sm">
          <div>
            {error && (
              <p className="text-danger-600 dark:text-danger-400">
                {error}
              </p>
            )}
            {!error && helpText && (
              <p className="text-slate-600 dark:text-slate-400">
                {helpText}
              </p>
            )}
          </div>

          <p className={`${counterColor} font-medium`}>
            {remaining} character{remaining === 1 ? '' : 's'} remaining
          </p>
        </div>
      </div>
    );
  }
);

TextareaWithCount.displayName = 'TextareaWithCount';
